import React from 'react';
import {
  InlineNotification,
  Button,
  SkeletonText,
  SkeletonPlaceholder
} from 'carbon-components-react';
import { Subtract16, Add16 } from '@carbon/icons-react';
import { AuthorisationType } from '../TransactionTypeStep';

const TRANSACTION_TYPE_LABELS: Record<AuthorisationType, string> = {
  WEB_ENQUIRY_AUTOMATION: 'Web flow enquiry lodgement',
  CREATE_ENQUIRY: 'API transactions'
};

interface Props {
  isLoading: boolean;
  paymentTypePrice: number;
  priceIncludeTax: boolean;
  quantity: number;
  onQuantityChange: (qty: number) => void;
  authorisationType: AuthorisationType;
}

const STEP = 1000;

export const MIN_TRANSACTION_QUANTITY = 1000;

const TransactionAmountStep: React.FC<Props> = ({
  isLoading,
  paymentTypePrice,
  priceIncludeTax,
  quantity,
  onQuantityChange,
  authorisationType
}) => {
  const totalPrice = (quantity * paymentTypePrice).toFixed(2);
  const transactionTypeLabel = TRANSACTION_TYPE_LABELS[authorisationType];

  const decrement = () => {
    if (quantity > MIN_TRANSACTION_QUANTITY) onQuantityChange(quantity - STEP);
  };

  const increment = () => {
    onQuantityChange(quantity + STEP);
  };

  return (
    <div className="transaction-amount-step">
      {/* Select transaction section */}
      <div className="transaction-amount-step--section">
        <div className="transaction-amount-step--section-heading">
          {isLoading ? <SkeletonText /> : 'Select transaction'}
        </div>
        <div className="transaction-amount-step--section-subheading">
          {isLoading ? (
            <SkeletonText />
          ) : (
            'Choose how many transactions you want to purchase:'
          )}
        </div>

        {isLoading ? (
          <SkeletonPlaceholder />
        ) : (
          <>
            <div className="transaction-amount-step--card">
              <div className="transaction-amount-step--row number-of-transactions">
                <span className="transaction-amount-step--row-label">
                  Number of transactions
                </span>
                <div className="transaction-amount-step--stepper">
                  <Button
                    size="small"
                    kind="tertiary"
                    renderIcon={Subtract16}
                    onClick={decrement}
                    hasIconOnly={true}
                  />
                  <div className="transaction-amount-step--stepper-value">
                    {quantity}
                  </div>
                  <Button
                    size="small"
                    kind="tertiary"
                    onClick={increment}
                    renderIcon={Add16}
                    hasIconOnly={true}
                  />
                </div>
              </div>

              <div className="transaction-amount-step--divider" />

              <div className="transaction-amount-step--row">
                <span className="transaction-amount-step--row-label">
                  Total transactions
                </span>
                <span className="transaction-amount-step--row-label large highlight">
                  {quantity}
                </span>
              </div>
            </div>
            <InlineNotification
              kind="info"
              lowContrast={true}
              title=""
              subtitle="Transaction bundles expire 1 year from purchase date. You'll receive email reminders before expiry."
              hideCloseButton={true}
              className="transaction-amount-step--notification"
            />
          </>
        )}
      </div>

      {/* Review pricing section */}
      {quantity > 0 && !isLoading && (
        <div className="transaction-amount-step--section">
          <div className="transaction-amount-step--section-heading">
            Review pricing
          </div>
          <div className="transaction-amount-step--section-subheading">
            Here&apos;s the pricing breakdown for your purchase:
          </div>

          <div className="transaction-amount-step--card">
            <div className="transaction-amount-step--row">
              <span className="transaction-amount-step--row-label">
                Transaction type
              </span>
              <span className="transaction-amount-step--row-label light highlight">
                {transactionTypeLabel}
              </span>
            </div>

            <div className="transaction-amount-step--row total-transactions">
              <span className="transaction-amount-step--row-label light">
                Total transactions
              </span>
              <span className="transaction-amount-step--row-label">
                {quantity}
              </span>
            </div>

            <div className="transaction-amount-step--divider no-top" />

            <div className="transaction-amount-step--row">
              <span className="transaction-amount-step--row-total">
                Total price
              </span>
              <span className="transaction-amount-step--row-total large highlight">
                ${totalPrice} AUD{priceIncludeTax ? ' (inc. GST)' : ''}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionAmountStep;
